import { Box, Button, useMediaQuery } from "@material-ui/core";
import { Link } from "react-router-dom";

import { Quote } from "../../../../api/Quote/Quote";
import { useQuoteInfoStyles } from "./quoteInfo.styles";
import { QuoteInfoSlider } from "./QuoteInfoSlider";
import { QuoteInfoTitle } from "./QuoteInfoTitle";
import { QuoteTotal } from "./QuoteTotal";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import QuoteDetailPriceOptions from "./QuoteDetailPriceOptions";
import { EQuoteStatus } from "../../../../api/Quote/EQuoteStatus";
import { quoteDepositAmount } from "../../../../app/utils";
import { PriceFormat } from "../../../../components/typography/PriceFormat";
import { useConfig } from "../../../../components/config/ConfigContext";
import { FeaturePaymentBalance } from "../../../../app/consts";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { fetchBalance } from "../../../../store/QuoteSlice";
import { RootState } from "../../../../app/rootReducer";

interface Props {
  quote: Quote;
}

export const QuoteInfo = ({ quote }: Props) => {
  const { isMoreThanMd, isMoreThanSm } = useBreakpoint();
  const classes = useQuoteInfoStyles();
  const matches = useMediaQuery("(max-width:1440px)");
  const { isFeatureEnabled } = useConfig();

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchBalance(quote.id));
  }, [quote.id, dispatch]);

  const { balanceLoading, balanceError } = useSelector((state: RootState) => state.quote);

  const isSolar = quote?.type?.includes("Solar");

  return (
    <Box
      bgcolor="#fff"
      display={"flex"}
      flexDirection={isMoreThanMd ? "row" : "column"}
      maxWidth={!matches ? "968px" : "968px"}
      margin={"0 auto"}
    >
      <Box width={isMoreThanMd ? "auto" : "100%"}>
        <Box className={classes.box}>
          <Box className={classes.boxClipPath}>
            <Box className={classes.boxContent}>
              <QuoteInfoSlider lineItems={quote.activeItems} />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box width={isMoreThanMd ? "auto" : "100%"} pl={isMoreThanMd ? "100px" : 0} mt={isMoreThanMd ? 0 : "30px"}>
        <Box maxWidth={isMoreThanSm ? "100%" : "272px"} mx={isMoreThanSm ? 0 : "auto"}>
          <QuoteInfoTitle quoteTitle={quote.title} />
        </Box>
        {isSolar ? (
          <QuoteTotal fullPrice={quote.fullPrice} price={quote.price} />
        ) : (
          <Box mt={isMoreThanSm ? "39px" : "25px"}>
            <Box
              fontSize={"12px"}
              textAlign={isMoreThanMd ? "left" : "center"}
              fontWeight={500}
              color={"#160037"}
              mb={"8px"}
            >
              Total payable to Swyft Energy
            </Box>
            <Box textAlign={isMoreThanMd ? "left" : "center"}>
              <Box fontSize={isMoreThanSm ? "22px" : "24px"} fontWeight={500} color={"#160037"}>
                <span style={{ borderBottom: "2px solid #160037" }}>
                  <PriceFormat value={quote.price} />
                </span>
              </Box>
            </Box>
            <Box
              mt={isMoreThanMd ? 0 : 1}
              mb={isMoreThanMd ? "41px" : "13px"}
              maxWidth={isMoreThanSm ? "100%" : "272px"}
              mx={isMoreThanSm ? 0 : "auto"}
            >
              <QuoteDetailPriceOptions quote={quote} textAlign={isMoreThanMd ? "left" : "center"} />
            </Box>
          </Box>
        )}
        <Box
          pr={isMoreThanMd ? 0 : isMoreThanSm ? "72px" : "0"}
          pl={isMoreThanMd ? 0 : isMoreThanSm ? "72px" : "0"}
          mt={isMoreThanSm ? "25px" : "40px"}
          maxWidth={isMoreThanSm ? "100%" : "272px"}
          mx={isMoreThanSm ? 0 : "auto"}
        >
          <Box
            boxShadow={"0 5px 15px 0 rgba(0, 0, 0, 0.07)"}
            height={"55px"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            borderRadius={"29px"}
            mb="17px"
          >
            <img src="images/google_logo.svg" alt="google" />
            <Box fontSize={isMoreThanSm ? "28px" : "20px"} color="#1e073f" ml="4px" mt="4px" mr="7px">
              4.8
            </Box>
            <img src="images/5star_gold.svg" alt="google" height={isMoreThanSm ? "15px" : "12px"} />

            <Box color="#4b5462" fontSize={"12px"} mt="6px" ml="13px">
              <a
                href="https://www.google.com/search?q=swyft+energy+dublin&ei=pTwkZPndLbPA8gKByKewDA&ved=0ahUKEwi5xKDznoH-AhUzoFwKHQHkCcYQ4dUDCA8&uact=5&oq=swyft+energy+dublin&gs_lcp=Cgxnd3Mtd2l6LXNlcnAQAzILCC4QgAQQxwEQrwEyBggAEAcQHjIICAAQigUQhgMyCAgAEIoFEIYDMggIABCKBRCGAzoKCAAQRxDWBBCwA0oECEEYAFDOCljOCmCqD2gBcAF4AIABNYgBNZIBATGYAQCgAQHIAQjAAQE&sclient=gws-wiz-serp#lrd=0x486712ba8bc294d1:0x4d83f2e0cb44d117,1,,,,"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#4b5462" }}
              >
                500+ Reviews
              </a>
            </Box>
          </Box>

          {quote.status === EQuoteStatus.Sent && (
            <>
              <Button
                component={Link}
                to={`/checkout/${quote.id}`}
                variant="contained"
                color="secondary"
                className={classes.btnBookNow}
              >
                Book Now
              </Button>
              <Box mt={"15px"} fontSize={"12px"} color={"#4b5462"} textAlign={"center"}>
                Book your installation with a €{quoteDepositAmount(quote)} deposit to secure your price.
              </Box>
            </>
          )}

          {(quote.status === EQuoteStatus.Approved ||
            quote.status === EQuoteStatus.Payment ||
            quote.status === EQuoteStatus.Paid) &&
            isFeatureEnabled(FeaturePaymentBalance) &&
            !balanceLoading &&
            !balanceError &&
            // Disable for now
            false && (
              <>
                <Button
                  component={Link}
                  to={`/payment/${quote.id}`}
                  variant="contained"
                  color="secondary"
                  className={classes.btnBookNow}
                >
                  Make a Payment
                </Button>
              </>
            )}
        </Box>
      </Box>
    </Box>
  );
};
